import { Component, OnInit, ViewChild, ElementRef } from '@angular/core';
import { NgForm } from '@angular/forms';
import { NavController, NavParams, App } from 'ionic-angular';
import { PaytronBackendService } from '../../providers/paytron-backend/paytron-backend.service';
import { Utils } from '../../providers/paytron-backend/utils';
import { Observable } from 'rxjs/Observable';
import { User } from '../../app/models/User';
import 'rxjs/add/operator/map';
import { Gender } from '../../app/models/Gender';
import { ModalDirective } from 'ngx-bootstrap/modal';
import { HttpErrorResponse } from '@angular/common/http';
import { NavControllerService } from '../../providers/nav-controller/navControllerService';
import { TranslateService } from '@ngx-translate/core';
import { AlertsHolder } from '../../app/models/AlertsHolder';
import { BsDatepickerConfig } from 'ngx-bootstrap';
import { Config } from '../../providers/config';
import { ConfigService } from '../../providers/config/config.service';
import { CurrentAccount } from '../../app/models/CurrentAccount';
import { AccountHolder } from '../../app/models/AccountHolder';
import { Storage } from '../../../node_modules/@ionic/storage';
import { UserService } from '../../services/user.service';
import { usersTableConfig } from './usersTableConfig';
import { GenericQueryParams } from '../../utils/utils';
import { TableService } from '../../services/table.service';

@Component({
  selector: 'page-users',
  templateUrl: 'users.html',
})
export class UsersPage implements OnInit {
  @ViewChild('userModal')
  private bootstrapModal: ModalDirective;

  @ViewChild('usernameInput')
  usernameInput: ElementRef;

  @ViewChild('resetPassModal')
  private resetPassModal: ModalDirective;

  @ViewChild('deleteModal')
  private confirmModal: ModalDirective;

  @ViewChild('anonymizeModal')
  private anonymizeModal: ModalDirective;

  @ViewChild('customerForm')
  private customerForm: NgForm;

  @ViewChild('resetPassForm')
  private resetPassForm: NgForm;

  @ViewChild('confirmResetModal')
  private confirmResetModal: ModalDirective;

  private pageName: string = 'page-users';

  private genders = Gender;
  private currentUser: User = new User();
  private edit = false;
  private dateOfBirth = new Date();
  private matchPassword: string;
  private initialValue: string;
  private userNames: string[] = [];

  private alertsHolder: AlertsHolder;

  private navControllerService: NavControllerService;
  public bsConfig: Partial<BsDatepickerConfig> = new BsDatepickerConfig();
  private currentConfiguration: Config;
  public tableHeaders = usersTableConfig;

  constructor(
    public navCtrl: NavController,
    public navParams: NavParams,
    private backendService: PaytronBackendService,
    private utils: Utils,
    private app: App,
    private translate: TranslateService,
    private configService: ConfigService,
    private storage: Storage,
    private userService: UserService,
    private tableService: TableService
  ) {
    this.navControllerService = new NavControllerService(app, storage);
    this.alertsHolder = new AlertsHolder(translate);
    this.currentConfiguration = this.configService.getConfiguration();
  }

  ngOnInit(): void {
    this.bootstrapModal.config = this.currentConfiguration.modalConfig;
    this.bsConfig = this.currentConfiguration.datepickerConfig;
    this.bootstrapModal.onShown.subscribe(() => this.utils.focus(this.usernameInput));
  }

  usersQueryFn() {
    return (params: GenericQueryParams) => this.userService.getPaginatedUsers(params);
  }

  submit(btnEvent: HTMLButtonElement) {
    btnEvent.disabled = true;
    this.currentUser.dateOfBirth = this.dateOfBirth ? this.dateOfBirth.toISOString() : null;
    let response = this.backendService.saveOrUpdateUser(this.currentUser);

    response
      .subscribe(
        (data) => {
          this.alertsHolder.show('User successfully saved', 'success');
        },
        (err: HttpErrorResponse) => {}
      )
      .add(() => {
        this.closeModal();
        btnEvent.disabled = false;
      });
  }

  submitAndCreateCurrentAccount(btnEvent: HTMLButtonElement) {
    btnEvent.disabled = true;
    this.currentUser.dateOfBirth = this.dateOfBirth ? this.dateOfBirth.toISOString() : null;
    this.backendService
      .saveOrUpdateUser(this.currentUser)
      .subscribe(
        (user) => {
          let account = new CurrentAccount();
          account.accountHolderUuid = user.uuid;
          account.accountHolderType = AccountHolder.User;
          this.backendService.saveOrUpdateCurrentAccount(account).subscribe((currentAccount) => {
            this.showCurrentAccountDetails(currentAccount);
          });
        },
        (err: HttpErrorResponse) => {}
      )
      .add(() => {
        btnEvent.disabled = false;
      });
  }

  submitResetPass(btnEvent: HTMLButtonElement) {
    this.backendService
      .resetPassword(this.currentUser)
      .subscribe(
        (user: User) => {
          this.alertsHolder.show('Password successfully resetted', 'success');
        },
        (err: HttpErrorResponse) => {}
      )
      .add(() => {
        this.closeResetPassModal();
        this.closeConfirmResetModal();
        btnEvent.disabled = false;
      });
  }

  addUser() {
    this.edit = false;
    this.currentUser = new User();
    this.customerForm.resetForm(this.currentUser);
    this.initialValue = null;
    this.showModal();
  }

  editUser(user) {
    this.edit = true;
    this.currentUser = user;
    this.initialValue = this.currentUser.userName;
    this.dateOfBirth = this.currentUser.dateOfBirth ? new Date(this.currentUser.dateOfBirth) : null;
    this.showModal();
  }

  deleteUser() {
    this.backendService
      .deleteUser(this.currentUser)
      .subscribe(
        (data) => {
          this.tableService.refetch();

          // this.getUsers();
          this.alertsHolder.show('User successfully deleted', 'success');
        },
        (err: HttpErrorResponse) => {
          this.alertsHolder.show(err.status.toString() + ':' + err.error.message, 'danger');
        }
      )
      .add(() => {
        this.closeConfirmModal();
      });
  }

  anonymizeUser(btnEvent: HTMLButtonElement) {
    btnEvent.disabled = true;
    const anonymousString: string =
      'private' +
      Array.from(this.currentUser.uuid).reduce<number>(
        (acc: number, val: string) => (acc = (31 * acc + val.charCodeAt(0)) & 0xffffffff),
        0
      );
    this.currentUser.firstName = anonymousString;
    this.currentUser.lastName = anonymousString;
    this.currentUser.userName = anonymousString;
    this.currentUser.telephoneNumber = null;
    this.currentUser.email = null;
    this.currentUser.address = null;
    this.currentUser.city = null;
    this.currentUser.province = null;
    this.currentUser.cap = null;
    this.currentUser.country = null;
    this.currentUser.dateOfBirth = null;
    this.currentUser.gender = Gender.not_specified;
    const saveOrUpdateUserObservable: Observable<User> = this.backendService.saveOrUpdateUser(this.currentUser);
    this.currentUser.gender = null;
    saveOrUpdateUserObservable
      .subscribe(
        (data) => {
          this.currentUser = data;
          this.tableService.refetch();
          this.alertsHolder.show('User successfully anonymized', 'success');
        },
        (err: HttpErrorResponse) => {
          this.alertsHolder.show(err.status.toString() + ':' + err.error.message, 'danger');
        }
      )
      .add(() => {
        btnEvent.disabled = true;
        this.closeAnonymizeModal();
      });
  }

  showConfirmModal(user) {
    this.currentUser = user;
    this.confirmModal.show();
  }

  closeConfirmModal() {
    this.confirmModal.hide();
  }

  showAnonymizeModal(user) {
    this.currentUser = user;
    this.anonymizeModal.show();
  }

  closeAnonymizeModal() {
    this.anonymizeModal.hide();
  }

  showModal() {
    this.bootstrapModal.show();
  }

  closeModal() {
    // this.getUsers();
    this.bootstrapModal.hide();
  }

  showResetPassModal(user) {
    this.currentUser = user;
    this.resetPassForm.reset();
    this.resetPassModal.show();
  }

  closeResetPassModal() {
    this.resetPassModal.hide();
  }

  showConfirmResetModal() {
    this.confirmResetModal.show();
  }

  closeConfirmResetModal() {
    this.confirmResetModal.hide();
  }

  showUserDetailsPage(user: User) {
    this.navControllerService.showUserDetailsPage(user.uuid);
  }

  showCurrentAccountDetails(currentAccount: CurrentAccount) {
    this.navControllerService.showCurrentAccountDetails(currentAccount.uuid);
  }

  getTitle(msg: string) {
    return this.translate.instant(msg);
  }
}



// WEBPACK FOOTER //
// ./src/pages/users/users.ts